// components/VoiceOrb.tsx — voice presence orb (redesign spec §4.6 / blueprint C4).
//
// V28-C. The Claude-style live-voice presence orb: a brand-gradient circle that
// BREATHES (gentle scale pulse) inside a soft radial HALO that pulses with it.
// "No caption — the animation speaks" (spec §4.6). Maps verbatim to the prototype
// `.orb` (docs/specs/v28-prototype/index.html 958-989) and the bottom-control orb
// of the voicecall screen (docs/specs/v28-prototype/screens/voicecall.html).
//
// Two sizes: default 120px, `sm` 84px (the in-call control-bar variant). The
// brand gradient comes from tokens (light-only build — tokens.mode resolves
// "light"); the violet shadow/halo values are lifted verbatim from the mockup.
//
// MOTION: the breathe scale-pulse + the halo pulse are gated by useReducedMotion()
// (state/use-reduced-motion.ts) — OS prefers-reduced-motion OR the in-app a11y
// toggle. When motion is reduced the orb renders fully static (no animation, no
// transform), and the halo holds a calm mid-opacity glow. The keyframes
// (orb-breathe / orb-halo) live in styles/global.css.

import type { JSX } from 'react';
import { useTokens } from '../state/theme';
import { useReducedMotion } from '../state/use-reduced-motion';

export interface VoiceOrbProps {
  /** Size variant: default 120px, `sm` = the 84px in-call control-bar orb. */
  size?: 'default' | 'sm';
}

// Lifted verbatim from the prototype (.orb / .orb::before). The breathe/halo
// timings match index.html 969/978; the violet shadow + radial halo match
// 964/977. Light-only, so these are fixed brand-violet values.
const PX = { default: 120, sm: 84 } as const;
const HALO_INSET = -18;                                          // .orb::before { inset: -18px }
const ORB_SHADOW = '0 10px 30px rgba(105, 67, 255, 0.35)';       // .orb box-shadow
const HALO_BG = 'radial-gradient(circle, rgba(105,67,255,0.28), transparent 68%)'; // .orb::before bg
const ANIM_DURATION = '3.4s';                                    // both keyframes
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';                     // prototype var(--ease)

/**
 * Brand-gradient voice presence orb with a breathe pulse + soft radial halo.
 * Decorative (aria-hidden) — the surrounding call chrome carries the live status
 * text, so the orb adds no duplicate label to the a11y tree.
 */
export function VoiceOrb({ size = 'default' }: VoiceOrbProps): JSX.Element {
  const tokens = useTokens();
  const reducedMotion = useReducedMotion();
  const px = PX[size];

  return (
    <span
      aria-hidden="true"
      data-testid="voice-orb"
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: px,
        height: px,
        flexShrink: 0,
      }}
    >
      {/* soft radial halo (the prototype's .orb::before) — pulses with the orb,
          or holds a calm static glow when motion is reduced. */}
      <span
        style={{
          position: 'absolute',
          inset: HALO_INSET,
          borderRadius: '50%',
          background: HALO_BG,
          zIndex: 0,
          // static mid-state when reduced; else the orb-halo opacity/scale pulse.
          opacity: reducedMotion ? 0.6 : undefined,
          animation: reducedMotion ? 'none' : `orb-halo ${ANIM_DURATION} ease-in-out infinite`,
        }}
      />

      {/* the gradient circle itself — breathes (scale pulse) unless reduced. */}
      <span
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: px,
          height: px,
          borderRadius: '50%',
          background: tokens.brandGradient,
          boxShadow: ORB_SHADOW,
          color: '#fff',
          animation: reducedMotion ? 'none' : `orb-breathe ${ANIM_DURATION} ${EASE} infinite`,
        }}
      >
        {/* presence glyph — a microphone (.orb svg is 44% of the circle). */}
        <svg
          viewBox="0 0 24 24"
          width="44%"
          height="44%"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          focusable="false"
        >
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      </span>
    </span>
  );
}
